import { useTranslation } from 'react-i18next';
import { Button } from '@scio3/ui';
import { ScreenCountSelector } from '../../components/ScreenCountSelector';

type Props = {
  screenCount: number;
  onScreenCountChange: (value: number) => void;
  minScreens: number;
  /** For trial users: "Your Plan: Pro Plus $8.33/screen/month" */
  planInfoText: string | undefined;
  /** Whether user is on a paid (non-trial) plan */
  isPaid: boolean;
  /** Plan name for paid users (e.g. "Pro Plus") */
  planName?: string;
  /** Price text for paid users (e.g. "$162.00/screen/per year") */
  planPriceText?: string;
  /** Called when paid user clicks "(change)" to go to plan selection */
  onChangePlan?: () => void;
  onDecreaseBlocked: () => void;
};

/**
 * Step 1: Number of Screens
 * - Trial: shows plan info text below spinner
 * - Paid: shows "Your Plan: [name] [price] (change)" with link to plan step
 */
export function WizardStepScreens({
  screenCount,
  onScreenCountChange,
  minScreens,
  planInfoText,
  isPaid,
  planName,
  planPriceText,
  onChangePlan,
  onDecreaseBlocked,
}: Props) {
  const { t } = useTranslation();

  // For paid users, build the plan info with "(change)" link
  const paidPlanInfo =
    isPaid && planName ? (
      <span>
        {t('Your Plan')}: {planName} {planPriceText}
        {onChangePlan && (
          <>
            {' '}
            (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="text-primary cursor-pointer h-auto p-0"
              onClick={onChangePlan}
            >
              {t('change')}
            </Button>
            )
          </>
        )}
      </span>
    ) : undefined;

  return (
    <div className="flex flex-col items-center py-6">
      <h1 className="text-xl font-semibold text-center mb-8">{t('Number of Screens')}</h1>
      <ScreenCountSelector
        value={screenCount}
        onChange={onScreenCountChange}
        min={minScreens}
        planInfoText={isPaid ? undefined : planInfoText}
        onDecreaseBlocked={onDecreaseBlocked}
      />
      {paidPlanInfo && (
        <p className="text-sm text-muted-foreground text-center mt-4">{paidPlanInfo}</p>
      )}
    </div>
  );
}
